import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { Gauge, gaugeClasses } from '@mui/x-charts/Gauge';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import ManagementApi from '../../../apis/ManagementApi';
import { LicenseStateDto } from '../../../generated-types/licenseStateDto';
import useApi from '../../../lib/useApi';
import LoadingCircle from '../../LoadingCircle/LoadingCircle';
import DataRefreshContext from '../lib/DataRefreshContext';
import ManagementGridProps from '../lib/ManagementGridProps';
import ErrorInfoBlock from './ErrorInfoBlock';

/**
 * LicenseCard Component
 *
 * This component displays the current license usage of an organization using a gauge chart.
 * It fetches the license data from the backend and visually represents the used and owned licenses.
 * Additionally, it provides tooltips and icons to indicate low or very low license availability.
 */
const LicenseCard = (props: ManagementGridProps) => {
  // State to hold the license data fetched from the backend
  const [licenseState, setLicenseState] = useState<LicenseStateDto | null>(
    null
  );

  // State to manage the loading state of the data fetch
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // State to handle any errors that occur during data fetching
  const [error, setError] = useState<boolean>(false);

  // Access the theme to utilize theme-based colors
  const theme = useTheme();

  // Thresholds to determine license availability statuses
  const lowAvailable = 0.8; // 80% usage indicates low availability
  const veryLowAvailable = 0.95; // 95% usage indicates very low availability

  // Initialize ManagementApi using the custom useApi hook for API interactions
  const managementApi = useApi(ManagementApi);

  // Access the dataRefreshCounter from the context
  const { dataRefreshCounter } = useContext(DataRefreshContext);

  /**
   * Fetches the license state from the backend
   */
  const fetchLicenseState = useCallback(async () => {
    setIsLoading(true); // Start loading
    setError(false); // Reset error state

    try {
      // Fetch the license state from the backend API
      const state = props.orgId
        ? await managementApi.GetLicenseQuantityWithOrgId(props.orgId)
        : await managementApi.GetLicenseQuantity();
      setLicenseState(state); // Update the license state with fetched data
    } catch (err) {
      console.error('Error fetching license state:', err);
      setError(true); // Set error state if fetching fails
    } finally {
      setIsLoading(false); // Stop loading
    }
  }, [managementApi, props.orgId]);

  /**
   * Fetches the license state when the component mounts.
   */
  useEffect(() => {
    fetchLicenseState();
  }, [fetchLicenseState]);

  /**
   * Fetch license data when dataRefreshCounter changes
   */
  useEffect(() => {
    fetchLicenseState();
  }, [dataRefreshCounter, fetchLicenseState]);

  /**
   * useMemo Hook for License Percentage
   *
   * Calculates the percentage of licenses used out of the total owned.
   * Memoizes the value to optimize performance by avoiding unnecessary recalculations.
   */
  const licensePercent = useMemo(() => {
    if (licenseState == null || licenseState.owned === 0) return 0;
    return licenseState.used / licenseState.owned;
  }, [licenseState]);

  /**
   * useMemo Hook for Gauge Color
   *
   * Determines the color of the gauge based on the license usage percentage.
   * - Error for very low availability
   * - Warning for low availability
   * - Primary color for normal usage
   */
  const gaugeColor = useMemo(() => {
    if (licensePercent >= veryLowAvailable) {
      return theme.palette.error.main; // Error color for very low availability
    }

    if (licensePercent >= lowAvailable) {
      return theme.palette.warning.main; // Warning color for low availability
    }

    return theme.palette.primary.main; // Default primary color for normal usage
  }, [
    licensePercent,
    theme.palette.error.main,
    theme.palette.primary.main,
    theme.palette.warning.main,
  ]);

  /**
   * The title to use for the tool tip, based on the license percent
   */
  const tooltipTitle = useMemo(() => {
    if (licensePercent == 1) return 'Out of Licenses';
    if (licensePercent >= veryLowAvailable) return 'Almost Out of Licenses';
    if (licensePercent >= lowAvailable) return 'License Availability is Low';
    return '';
  }, [licensePercent]);

  return (
    <>
      {/* 
        Conditional Rendering:
        - If there's an error, display the ErrorInfoBlock component.
        - Otherwise, display the license gauge and related information.
      */}
      {error ? (
        <ErrorInfoBlock
          title="License Data"
          subtitle="Failed to load license data."
          baseId="error-license-state-block"
        />
      ) : (
        <>
          <Box
            position="relative"
            height="100%"
            width="100%"
            padding="1em"
            display="flex"
            flexDirection="column"
            alignItems="center"
            justifyContent="space-around"
          >
            {/* 
              Gauge Component:
              - Displays used licenses out of owned licenses.
              - Styled to reflect the current license usage percentage.
              - Text inside the gauge shows "Used / Owned" licenses.
            */}
            <Gauge
              height={150}
              value={licenseState?.used ?? 0} // Current used licenses
              valueMax={licenseState?.owned ?? 0} // Total owned licenses
              startAngle={-110}
              endAngle={110}
              sx={{
                [`& .${gaugeClasses.valueText}`]: {
                  fontSize: 30, // Font size for the gauge text
                  transform: 'translate(0px, 0px)', // Text positioning
                },
                [`& .${gaugeClasses.valueArc}`]: {
                  fill: gaugeColor, // Dynamic color based on license usage
                },
              }}
              text={({ value, valueMax }) => `${value} / ${valueMax}`} // Text displayed in the gauge
            />

            {/* 
              Tooltip with License Usage Status:
              - Displays an icon with a tooltip indicating the license availability status.
              - Icons change based on the licensePercent thresholds.
            */}
            <Tooltip
              title={tooltipTitle}
              arrow
              sx={{
                cursor: licensePercent < lowAvailable ? 'initial' : 'help', // Cursor style based on availability
              }}
            >
              <Box display="flex" alignItems="center" gap=".5em">
                <Typography variant="h5">Licenses Used</Typography>

                {/* 
                  Conditional Icons:
                  - Shows a error icon if license availability is very low.
                  - Shows a warning icon if license availability is low.
                  - Shows nothing if license availability is normal.
                */}
                {licensePercent >= veryLowAvailable ? (
                  <ErrorOutlineRoundedIcon color="error" />
                ) : licensePercent >= lowAvailable ? (
                  <WarningAmberRoundedIcon color="warning" />
                ) : (
                  <></> // Empty fragment when no icon is needed
                )}
              </Box>
            </Tooltip>
            <LoadingCircle isLoading={isLoading} hideText />
          </Box>
        </>
      )}
    </>
  );
};

export default LicenseCard;
